<!--
  App.vue - Root Application Component

  @version 3.0.1
  @description Main application shell with PWA support, snackbar notifications,
               error boundaries, and accessibility features
  @migrated Vue 3.5 Composition API + TypeScript strict + Vuetify 3.5

  @changelog v3.0.0 → v3.0.1: BUGFIX iOS Safari layout-shift al primo render.
    `.app-root` usava `min-height: -webkit-fill-available` come fix legacy per
    il problema di `100vh` su iOS. Ma `-webkit-fill-available` su iOS Safari
    è valutato UNA SOLA VOLTA al primo render, basandosi sulla layout viewport
    corrente (con URL bar visibile). Non si aggiorna quando Safari nasconde
    dinamicamente la URL bar al primo scroll, causando layout-shift visibile:
    le `.mobile-bottom-fixed` (position: fixed; bottom: 0) sembrano "alzate"
    rispetto al fondo del visual viewport finché l'utente non scrolla.
    Fix: sostituito con `100dvh` (dynamic viewport height), che si aggiorna
    in tempo reale durante l'animazione di hide/show della URL bar.
    Supporto: iOS Safari 15.4+, Chrome 108+, Firefox 101+, Edge 108+.
    `100vh` mantenuto come fallback per browser più vecchi.
-->
<template>
  <div
    :class="rootClasses"
    :data-theme="currentTheme"
    :data-version="appVersion"
  >
    <!-- Skip Link for Accessibility -->
    <a
      href="#main-content"
      class="skip-link"
      @click.prevent="skipToContent"
    >
      {{ t('Skip to main content') }}
    </a>

    <v-app>
      <!-- Error Boundary Fallback -->
      <template v-if="hasError">
        <v-container class="fill-height">
          <v-row align="center" justify="center">
            <v-col cols="12" md="6">
              <v-alert
                type="error"
                prominent
                border="start"
                :title="t('Application Error')"
              >
                <p>{{ t('An unexpected error occurred. Please try refreshing the page.') }}</p>
                <template #append>
                  <v-btn
                    variant="outlined"
                    color="error"
                    @click="handleErrorRecovery"
                  >
                    {{ t('Refresh') }}
                  </v-btn>
                </template>
              </v-alert>
              <v-expansion-panels v-if="errorDetails" class="mt-4">
                <v-expansion-panel :title="t('Error Details')">
                  <v-expansion-panel-text>
                    <pre class="text-caption">{{ errorDetails }}</pre>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <!-- Normal App Content -->
      <template v-else>
        <!-- Loading overlay during app refresh -->
        <v-overlay
          v-model="refreshingApp"
          class="align-center justify-center"
          persistent
          opacity="0.8"
          :z-index="2000"
        ></v-overlay>

        <!-- ══ Mobile Shell (< 960px) ═══════════════════════════════ -->
        <template v-if="showChrome && isMobileOrTablet">
          <!-- Top: color strip (browser) or dynamic theme-color (PWA) -->
          <app-mobile-status-bar :area="currentNavigation" />
        </template>

        <!-- ══ Desktop Shell (≥ 960px) ═══════════════════════════════ -->
        <!-- Navigation drawer (lazy loaded, desktop only) -->
        <app-navigation v-if="showChrome && isMobileOrTablet" v-model="mobileDrawerOpen" />

        <!-- Top toolbar (desktop only) -->
        <app-toolbar v-if="showChrome && isDesktop" />

        <!-- Main content area -->
        <v-main
          id="main-content"
          tabindex="-1"
          :class="{ 'main-content--mobile': isMobileOrTablet }"
        >
          <router-view />
        </v-main>

        <!-- Footer (desktop only) -->
        <app-footer v-if="showChrome && isDesktop" />

        <!-- Mobile Bottom Bars — teleported to <body> (v2.67.9).
             Vedi commento sotto per il razionale. -->
        <Teleport to="body">
          <div
            v-if="showChrome && isMobileOrTablet"
            :class="['mobile-bottom-fixed', 'v-theme--' + currentTheme]"
          >
            <app-mobile-quick-bar @open-drawer="mobileDrawerOpen = true" />
            <app-mobile-branded-bar :area="currentNavigation" />
          </div>
        </Teleport>

        <!-- Loading overlay -->
        <loading-overlay v-if="loading && splashShown" />

        <!-- Splash screen -->
        <splash-screen :model-value="!splashShown" />

        <!-- PWA update notification -->
        <new-content-available-toastr
          v-if="newContentAvailable"
          :refreshing-app="refreshingApp"
          @skip="handleDismissUpdate"
          @refresh="handleServiceWorkerUpdate"
        />

        <!-- Global Confirm Dialog Provider -->
        <confirm-dialog-provider />

        <!-- Visualizzatore PDF in overlay (PWA standalone, FE v2.174.0) -->
        <pdf-viewer-dialog />

        <!--
          Global Preferences dialog — mounted at root so it's reachable from
          BOTH the desktop user menu (AppToolbar) and the mobile drawer
          (AppNavigation), which are alternative for breakpoint and never
          mounted together. Open state lives in useAppStore.preferencesDialogOpen.
          The widget itself (GgPushNotificationsCard) is self-contained.
        -->
        <gg-dialog
          v-model="preferencesDialogOpen"
          :title="t('Preferenze')"
          :max-width="560"
          no-auto-fullscreen
        >
          <gg-push-notifications-card />
        </gg-dialog>

        <!--
          R2 — Notifiche su mobile: dialog a TUTTA PAGINA (GgDialog va
          fullscreen da sola sotto i 960px) aperta dalla campanella del drawer
          (AppNavigation), che si chiude nel frattempo. Montata qui perché il
          drawer è `v-if` e viene distrutto alla chiusura. Stato in
          useNotificationsStore.mobileDialogOpen.
        -->
        <gg-dialog
          v-if="showChrome && isMobileOrTablet"
          v-model="notificationsDialogOpen"
          :title="t('Notifiche')"
          :max-width="560"
        >
          <notifications-list hide-header @navigate="notificationsDialogOpen = false" />
        </gg-dialog>

        <!-- Snackbar Container (ARIA Live Region) -->
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          class="snackbar-announcer sr-only"
        >
          {{ activeSnackbarMessage }}
        </div>

        <!-- Success Snackbar -->
        <v-snackbar
          v-model="snackbarSuccessVisible"
          location="bottom"
          color="success"
          :timeout="SNACKBAR_TIMEOUT"
          @update:model-value="handleSnackbarSuccessClose"
        >
          <div class="d-flex align-center">
            <v-icon start>check-circle-outline</v-icon>
            {{ snackbarMessage }}
          </div>
          <template #actions>
            <gg-btn
              variant="text"
              icon="close"
              size="small"
              :aria-label="t('Close')"
              @click="snackbarSuccessVisible = false"
            />
          </template>
        </v-snackbar>

        <!-- Error Snackbar -->
        <v-snackbar
          v-model="snackbarErrorVisible"
          location="bottom"
          color="error"
          :timeout="SNACKBAR_TIMEOUT"
          @update:model-value="handleSnackbarErrorClose"
        >
          <div class="d-flex align-center">
            <v-icon start>error-outline</v-icon>
            {{ snackbarMessage }}
          </div>
          <template #actions>
            <gg-btn
              variant="text"
              icon="close"
              size="small"
              :aria-label="t('Close')"
              @click="snackbarErrorVisible = false"
            />
          </template>
        </v-snackbar>

        <!-- Info Snackbar -->
        <v-snackbar
          v-model="snackbarInfoVisible"
          location="bottom"
          color="info"
          :timeout="SNACKBAR_TIMEOUT"
          @update:model-value="handleSnackbarInfoClose"
        >
          <div class="d-flex align-center">
            <v-icon start>info-outline</v-icon>
            {{ snackbarMessage }}
          </div>
          <template #actions>
            <gg-btn
              variant="text"
              icon="close"
              size="small"
              :aria-label="t('Close')"
              @click="snackbarInfoVisible = false"
            />
          </template>
        </v-snackbar>

        <!-- Offline Indicator -->
        <v-snackbar
          v-model="showOfflineIndicator"
          location="top"
          color="warning"
          :timeout="-1"
        >
          <div class="d-flex align-center">
            <v-icon start>wifi-off</v-icon>
            {{ t('You are offline. Some features may be unavailable.') }}
          </div>
        </v-snackbar>
      </template>
    </v-app>
  </div>
</template>

<script setup lang="ts">
/**
 * App.vue - Root Application Component
 *
 * @version 2.1.1
 * @description Main application shell providing:
 * - PWA update notifications
 * - Global snackbar notifications
 * - Error boundary with recovery
 * - Accessibility features (skip link, ARIA live)
 * - Offline detection
 * - Theme management
 *
 * @example
 * The App component is mounted automatically by main.ts.
 * It wraps all routes and provides global UI elements.
 */

import {
  ref,
  computed,
  watch,
  onMounted,
  onErrorCaptured,
  defineAsyncComponent,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from 'vuetify'
import { storeToRefs } from 'pinia'
import { useAppState } from '@/composables/useAppState'
import { useResponsive } from '@/composables/useResponsive'
import { usePwaDetection } from '@/composables/usePwaDetection'
import { useNavigationStore } from '@/stores/useNavigationStore'
import { useAppStore } from '@/stores/app'
import { BRAND_COLORS } from '@/plugins/vuetify'

// =============================================================================
// Component Options
// =============================================================================

defineOptions({
  name: 'GandolfiWebApp'
})

// =============================================================================
// Lazy Loaded Components
// =============================================================================

// Critical path components - loaded immediately
import SplashScreen from '@/components/SplashScreen.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import ConfirmDialogProvider from '@/components/ConfirmDialogProvider.vue'
import PdfViewerDialog from '@/components/ui/PdfViewerDialog.vue'
import GgPushNotificationsCard from '@/components/ui/GgPushNotificationsCard.vue'
import NotificationsList from '@/components/NotificationsList.vue'
import { useNotificationsStore } from '@/stores/useNotificationsStore'

// Non-critical components - lazy loaded
const AppToolbar = defineAsyncComponent(() =>
  import('@/components/AppToolbar.vue')
)
const AppFooter = defineAsyncComponent(() =>
  import('@/components/AppFooter.vue')
)
const AppNavigation = defineAsyncComponent(() =>
  import('@/components/AppNavigation.vue')
)
// CRITICAL: This component MUST NOT be lazy-loaded.
// It's the PWA update notification — if chunks are stale after a deploy,
// lazy-loading it fails (the very thing it's supposed to fix).
import NewContentAvailableToastr from '@/components/NewContentAvailableToastr.vue'

// Mobile shell components (lazy loaded - only needed on mobile)
const AppMobileStatusBar = defineAsyncComponent(() =>
  import('@/components/AppMobileStatusBar.vue')
)
const AppMobileQuickBar = defineAsyncComponent(() =>
  import('@/components/AppMobileQuickBar.vue')
)
const AppMobileBrandedBar = defineAsyncComponent(() =>
  import('@/components/AppMobileBrandedBar.vue')
)

// =============================================================================
// Constants
// =============================================================================

const SNACKBAR_TIMEOUT = 3000

// =============================================================================
// Composables
// =============================================================================

const { t } = useI18n()
const vuetifyTheme = useTheme()
const { isDesktop, isMobileOrTablet } = useResponsive()
const { setThemeColor } = usePwaDetection()
const navigationStore = useNavigationStore()
const { currentNavigation } = storeToRefs(navigationStore)

const appStoreInstance = useAppStore()
const { preferencesDialogOpen } = storeToRefs(appStoreInstance)
const { mobileDialogOpen: notificationsDialogOpen } = storeToRefs(useNotificationsStore())

const {
  appVersion,
  networkOnLine,
  splashShown,
  refreshingApp,
  isPageFullScreen,
  loading,
  snackbarMessage,
  showSnackbarSuccessMessage,
  showSnackbarErrorMessage,
  showSnackbarInfoMessage,
  newContentAvailable,
  serviceWorkerSkipWaiting,
  dismissServiceWorkerUpdate,
  resetSnackbarSuccess,
  resetSnackbarError,
  resetSnackbarInfo
} = useAppState()

// =============================================================================
// Local State
// =============================================================================

// Snackbar visibility
const snackbarSuccessVisible = ref(false)
const snackbarErrorVisible = ref(false)
const snackbarInfoVisible = ref(false)

// Error boundary
const hasError = ref(false)
const errorDetails = ref<string | null>(null)

// Mobile drawer
const mobileDrawerOpen = ref(false)

// Offline indicator
const showOfflineIndicator = ref(false)

// =============================================================================
// Computed
// =============================================================================

/**
 * Root element classes
 */
const rootClasses = computed(() => ({
  'app-root': true,
  'container-mobile': isMobileOrTablet.value,
  'network-offline': !networkOnLine.value,
  'app-fullscreen': isPageFullScreen.value
}))

/**
 * Current theme name
 */
const currentTheme = computed(() => vuetifyTheme.global.name.value)

/**
 * Whether to show navigation chrome (toolbar, footer, drawer)
 */
const showChrome = computed(() => !isPageFullScreen.value)

// =============================================================================
// Mobile — Dynamic Theme Color
// =============================================================================

const AREA_THEME_COLORS: Record<string, string> = {
  admin: BRAND_COLORS.ctf,
  commercial: BRAND_COLORS.mkt,
  tech: BRAND_COLORS.srv,
  cst: BRAND_COLORS.srv,
}

watch(currentNavigation, (area) => {
  if (isMobileOrTablet.value) {
    const color = AREA_THEME_COLORS[area] ?? BRAND_COLORS.primaryDark
    setThemeColor(color)
  }
}, { immediate: true })

/**
 * Active snackbar message for screen readers
 */
const activeSnackbarMessage = computed(() => {
  if (snackbarSuccessVisible.value || snackbarErrorVisible.value || snackbarInfoVisible.value) {
    return snackbarMessage.value
  }
  return ''
})

// =============================================================================
// Error Boundary
// =============================================================================

/**
 * Capture errors from child components
 */
onErrorCaptured((err: Error, instance, info: string) => {
  console.error('[App] Error captured:', err)
  console.error('[App] Component:', instance)
  console.error('[App] Info:', info)

  // If it's a chunk load error (stale deploy), force reload instead of showing error panel
  const isChunkError =
    err.message?.includes('Failed to fetch dynamically imported module') ||
    err.message?.includes('Importing a module script failed') ||
    err.message?.includes('error loading dynamically imported module')

  if (isChunkError) {
    console.warn('[App] Chunk load error in component, forcing reload...')
    window.location.reload()
    return false
  }

  hasError.value = true
  errorDetails.value = `${err.name}: ${err.message}\n\nComponent Info: ${info}\n\nStack:\n${err.stack || 'No stack trace'}`

  // Don't propagate the error
  return false
})

/**
 * Handle error recovery (refresh page)
 */
function handleErrorRecovery(): void {
  hasError.value = false
  errorDetails.value = null
  window.location.reload()
}

// =============================================================================
// Snackbar Handlers
// =============================================================================

function handleSnackbarSuccessClose(value: boolean): void {
  if (!value) {
    resetSnackbarSuccess()
  }
}

function handleSnackbarErrorClose(value: boolean): void {
  if (!value) {
    resetSnackbarError()
  }
}

function handleSnackbarInfoClose(value: boolean): void {
  if (!value) {
    resetSnackbarInfo()
  }
}

// =============================================================================
// PWA Handlers
// =============================================================================

async function handleServiceWorkerUpdate(): Promise<void> {
  await serviceWorkerSkipWaiting()
}

function handleDismissUpdate(): void {
  dismissServiceWorkerUpdate()
}

// =============================================================================
// Accessibility
// =============================================================================

/**
 * Skip to main content (accessibility)
 */
function skipToContent(): void {
  const main = document.getElementById('main-content')
  if (main) {
    main.focus()
    main.scrollIntoView({ behavior: 'smooth' })
  }
}

// =============================================================================
// Watchers
// =============================================================================

// Watch for snackbar triggers from store
watch(showSnackbarSuccessMessage, (newValue) => {
  if (newValue) {
    snackbarSuccessVisible.value = true
    resetSnackbarSuccess()
  }
})

watch(showSnackbarErrorMessage, (newValue) => {
  if (newValue) {
    snackbarErrorVisible.value = true
    resetSnackbarError()
  }
})

watch(showSnackbarInfoMessage, (newValue) => {
  if (newValue) {
    snackbarInfoVisible.value = true
    resetSnackbarInfo()
  }
})

// Watch network status
watch(networkOnLine, (online) => {
  if (!online) {
    showOfflineIndicator.value = true
  } else {
    showOfflineIndicator.value = false
  }
}, { immediate: true })

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(() => {
  // Log app version on mount
  console.log(`[App] Gandolfi CMS v${appVersion.value} initialized`)

  // Check initial network status
  if (!navigator.onLine) {
    showOfflineIndicator.value = true
  }
})
</script>

<style lang="scss">
@use '@/assets/scss/main.scss' as *;

// =============================================================================
// App Root Styles
// =============================================================================

.app-root {
  // ────────────────────────────────────────────────────────────────────
  // Viewport sizing (v3.0.1)
  //
  // The `.app-root` is the size anchor for the entire app shell: <v-main>,
  // .mobile-bottom-fixed (position: fixed; bottom: 0), and the page-level
  // scroll containers (e.g. .dashboard) all inherit/derive from this height.
  //
  // History:
  //   v1: `100vh` — broken on iOS Safari because vh includes the dynamic
  //       URL bar area. Result: visible content cut off at the bottom.
  //   v2: `-webkit-fill-available` (the old iOS Safari workaround) — better
  //       than 100vh, but evaluated ONCE at first render against the layout
  //       viewport with URL bar visible. Does NOT update when iOS Safari
  //       hides the URL bar on first scroll, causing visible layout-shift:
  //       .mobile-bottom-fixed appears "raised" until the user scrolls.
  //   v3 (current): `100dvh` (dynamic viewport height) — the spec-defined
  //       value that updates in real time as the URL bar is shown/hidden.
  //       This eliminates the layout-shift completely.
  //
  // Browser support for 100dvh:
  //   iOS Safari 15.4+ (Mar 2022), Chrome 108+ (Nov 2022),
  //   Firefox 101+ (Jun 2022), Edge 108+ (Nov 2022).
  // We keep `100vh` as a fallback declaration for any browser that doesn't
  // recognise dvh — such browsers will simply ignore the second statement.
  // ────────────────────────────────────────────────────────────────────
  min-height: 100vh;   // fallback for browsers without dvh support
  min-height: 100dvh;  // dynamic viewport height — source of truth

  // CSS Custom Properties for theming
  --app-transition-duration: 200ms;
  --app-transition-timing: ease;

  // Fullscreen mode
  &.app-fullscreen {
    .v-main {
      padding: 0 !important;
    }
  }

  // Offline visual indicator
  &.network-offline {
    // Subtle grayscale effect when offline
    filter: grayscale(0.1);
  }
}

// =============================================================================
// Skip Link (Accessibility)
// =============================================================================

.skip-link {
  position: absolute;
  top: -40px;
  left: 0;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  padding: 8px 16px;
  z-index: 9999;
  text-decoration: none;
  font-weight: 500;
  border-radius: 0 0 4px 0;
  transition: top var(--app-transition-duration) var(--app-transition-timing);

  &:focus {
    top: 0;
    outline: 2px solid rgb(var(--v-theme-on-primary));
    outline-offset: 2px;
  }
}

// =============================================================================
// Screen Reader Only
// =============================================================================

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// =============================================================================
// Snackbar Announcer (ARIA Live)
// =============================================================================

.snackbar-announcer {
  // Visually hidden but announced by screen readers
  @extend .sr-only;
}

// =============================================================================
// Error Details
// =============================================================================

pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(0, 0, 0, 0.05);
  padding: 16px;
  border-radius: 4px;
  font-size: 12px;
  max-height: 200px;
  overflow: auto;
}

// =============================================================================
// Main Content Focus
// =============================================================================

#main-content {
  &:focus {
    outline: none;
  }
}

// =============================================================================
// Mobile Main Content
// =============================================================================

// Fixed bottom bar container.
//
// v2.67.9 — il div con questa classe è ora montato via <Teleport to="body">
// (vedi App.vue template), quindi vive fuori da <v-app> e direttamente sotto
// <body>. Razionale: position: fixed con bottom: 0 deve ancorarsi al viewport
// reale, ma qualsiasi ancestor con `transform`, `filter`, `perspective`,
// `will-change`, `contain` o `backdrop-filter` crea un NUOVO containing block
// per i discendenti fixed, intrappolandoli al box dell'ancestor invece che al
// viewport (regressione osservata dopo deploy v2.67.0). Teleportando il nav a
// body lo rendiamo immune a qualsiasi proprietà containing-block-creating
// introdotta in futuro su `.app-root` / `.v-application` / `.v-application__wrap`.
// La classe `v-theme--<name>` è propagata dal template per preservare le CSS
// vars di tema, che altrimenti non cascadano fuori da `.v-application`.
//
// The background-color (set via --bottom-bar-color CSS var) fills the entire
// fixed area including the iOS safe zone below the branded bar.
.mobile-bottom-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: $z-index-fixed;
  display: flex;
  flex-direction: column;
}

.main-content--mobile {
  // Remove Vuetify's default padding (toolbar height) since mobile has no app-bar
  // In PWA standalone, add safe-area-inset-top to clear the status bar color strip
  padding-top: calc(8px + env(safe-area-inset-top, 0px)) !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

// Scroll clearance: transparent spacer at the end of every scrollable container.
// This lets the last content item scroll ABOVE the fixed bottom bars.
// 56px quick bar + 32px branded bar + safe area (home indicator on notched devices)
//
// Also: `overscroll-behavior-y: contain` on the scrollable v-container disables
// the browser's native overscroll bounce on iOS Safari and rubber-band effect on
// Android Chrome, so the custom pull-to-refresh (GgPullToRefresh) is the only
// vertical-overscroll behaviour at the top of mobile lists.
.container-mobile {
  .v-main .v-container {
    overscroll-behavior-y: contain;

    &::after {
      content: '';
      display: block;
      min-height: calc(88px + env(safe-area-inset-bottom, 0px));
      flex-shrink: 0;
      pointer-events: none;
    }
  }
}

// =============================================================================
// INVARIANT v2.67.9 — Containing block protection per `.mobile-bottom-fixed`
// =============================================================================
//
// MAI aggiungere su `.app-root`, `.v-application`, o `.v-application__wrap`
// (cioè sugli ancestor montati sopra al <v-app> root) nessuna delle seguenti
// proprietà:
//   transform, filter, perspective, will-change (!= auto), contain (!= none),
//   backdrop-filter.
//
// Qualunque di queste crea un NUOVO containing block per i discendenti
// `position: fixed`, intrappolandoli al box dell'ancestor invece che al
// viewport (è stata la causa probabile del bug bottom-nav sticky-mid-screen
// osservato dopo v2.67.0). Il bottom nav ora è protetto da <Teleport to="body">
// nel template, ma altri elementi `position: fixed` rendered DENTRO al v-app
// (es. il FAB di Agenda `.gg-mobile-fab`, le snackbar, le dialog Vuetify) NON
// lo sono. Se serve `transform` per una transizione, scoparlo a un wrapper
// child dedicato dentro la view, mai sul root dell'app shell.
</style>
